import React, { useEffect, useRef, useState } from 'react';
import { Form, Formik } from 'formik';
import { Link, useNavigate } from 'react-router-dom';
import * as yup from 'yup';
import styled from 'styled-components';
import * as api from '../../services/api';
import { setAuthToken } from '../../utils/auth';
import { getTermsAgreementStatus, setTermsAgreementStatus, hasUserAgreedToTerms, clearTermsAgreementStatus } from '../../utils/termsAgreement';
import TermsAndConditionsModal from '../../components/TermsAndConditionsModal';

const Wrapper = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5f5f5;
  padding: 24px;
`;

const Card = styled.div`
  width: 100%;
  max-width: 440px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  padding: 40px;
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 600;
  color: #1a1a1a;
  text-align: center;
  margin: 0 0 24px;
`;

const Label = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: #333;
  margin-bottom: 6px;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 14px;
  font-size: 16px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  box-sizing: border-box;
  margin-bottom: 16px;
  &:focus {
    outline: none;
    border-color: #3b82f6;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 12px 14px;
  font-size: 16px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  margin-bottom: 16px;
  box-sizing: border-box;
`;

const ErrorText = styled.div`
  color: #dc2626;
  font-size: 12px;
  margin-top: -8px;
  margin-bottom: 12px;
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: 12px 20px;
  font-size: 16px;
  font-weight: 600;
  color: white;
  background: #2563eb;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  margin-top: 8px;
  &:hover {
    background: #1d4ed8;
  }
  &:disabled {
    background: #9ca3af;
    cursor: not-allowed;
  }
`;

const NpiRow = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 4px;
`;

const NpiInput = styled.input`
  flex: 1;
  padding: 12px 14px;
  font-size: 16px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  box-sizing: border-box;
  &:focus {
    outline: none;
    border-color: #3b82f6;
  }
`;

const NpiVerifyButton = styled.button`
  padding: 12px 16px;
  font-size: 14px;
  font-weight: 600;
  color: white;
  background: #059669;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  white-space: nowrap;
  &:hover {
    background: #047857;
  }
  &:disabled {
    background: #9ca3af;
    cursor: not-allowed;
  }
`;

const NpiInfoBox = styled.div`
  background: #ecfdf5;
  border: 1px solid #6ee7b7;
  border-radius: 6px;
  padding: 10px 14px;
  margin-bottom: 16px;
  font-size: 13px;
  color: #065f46;
  line-height: 1.5;
`;

const TermsLink = styled.button`
  background: none;
  border: none;
  color: #2563eb;
  text-decoration: underline;
  cursor: pointer;
  font-size: 14px;
  padding: 0;
  margin-left: 4px;
`;

const BackLink = styled(Link)`
  display: block;
  text-align: center;
  margin-top: 16px;
  font-size: 14px;
  color: #6b7280;
  text-decoration: none;
  &:hover {
    color: #374151;
  }
`;

const getValidationSchema = () =>
  yup.object({
    first_name: yup.string().required('First name is required'),
    last_name: yup.string().required('Last name is required'),
    email: yup.string().required('Email is required').email('Invalid email'),
    group_name: yup
      .string()
      .trim()
      .when('tier_id', {
        is: (tid: number) => tid === 3 || tid === 4,  // Small Business, Enterprise
        then: (s) => s.required('Business or group name is required for Small Business and Enterprise'),
        otherwise: (s) => s.optional(),
      }),
    password: yup.string().required('Password is required').min(8, 'At least 8 characters'),
    confirm_password: yup.string().required('Confirm password').oneOf([yup.ref('password')], 'Passwords must match'),
    tier_id: yup.number().required('Select a tier').min(1).max(4),
  });

interface Tier {
  id: number;
  name: string;
  description: string;
}

const SuccessText = styled.div`
  color: #059669;
  font-size: 14px;
  margin-bottom: 16px;
`;

const Signup: React.FC = () => {
  const navigate = useNavigate();
  const [tiers, setTiers] = useState<Tier[]>([]);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [termsVersion, setTermsVersion] = useState('1.2');
  const [isSubmitting, setIsSubmitting] = useState(false);
  /** Set when verification email was sent; grays out Sign Up button. */
  const [emailSent, setEmailSent] = useState(false);
  /** Email we sent verification to; used for polling and redirect. */
  const pendingVerifyEmailRef = useRef<string | null>(null);
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  /** Enterprise contact form (tier 4): contact email, phone, submit state. */
  const [enterpriseEmail, setEnterpriseEmail] = useState('');
  const [enterprisePhone, setEnterprisePhone] = useState('');
  const [enterpriseSubmitting, setEnterpriseSubmitting] = useState(false);
  const [enterpriseSuccess, setEnterpriseSuccess] = useState(false);
  const [enterpriseError, setEnterpriseError] = useState('');
  /** NPI verification state */
  const [npiInput, setNpiInput] = useState('');
  const [npiData, setNpiData] = useState<null | {
    npi_number: string; npi_status: string; npi_type: string;
    npi_specialty: string; npi_state: string; npi_credential: string; npi_display_name: string;
  }>(null);
  const [npiVerifying, setNpiVerifying] = useState(false);
  const [npiError, setNpiError] = useState('');

  /** Require ToS every time: clear any prior agreement on signup page load so user must open modal and click Agree. */
  useEffect(() => {
    clearTermsAgreementStatus();
  }, []);

  useEffect(() => {
    api.getTiers().then((res) => {
      if (res?.success && Array.isArray(res.data)) setTiers(res.data);
    }).catch(() => setTiers([]));
    api.getTermsVersion().then((res) => {
      if (res?.success && res?.data?.terms_version) setTermsVersion(res.data.terms_version);
    }).catch(() => {});
  }, []);

  /** When user clicks Agree in ToS modal: log consent (timestamp) and close modal; does not send email. */
  const handleAgreeTerms = () => {
    setTermsAgreementStatus(true, termsVersion);
    setShowTermsModal(false);
  };

  /** Poll check-verified every 3s; when verified, redirect to login. */
  useEffect(() => {
    const email = pendingVerifyEmailRef.current;
    if (!email || !emailSent) return;
    const poll = () => {
      api.checkSignupVerified(email).then((res) => {
        if (res?.verified) {
          if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
          }
          navigate('/login', { replace: true });
        }
      }).catch(() => {});
    };
    poll();
    pollIntervalRef.current = setInterval(poll, 3000);
    return () => {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    };
  }, [emailSent, navigate]);

  const handleHideTermsModal = () => {
    setShowTermsModal(false);
  };

  const submitSignup = (values: {
    first_name: string;
    last_name: string;
    email: string;
    group_name: string;
    password: string;
    tier_id: number;
  }) => {
    const terms = getTermsAgreementStatus();
    setError('');
    setIsSubmitting(true);
    api
      .signUp({
        first_name: values.first_name,
        last_name: values.last_name,
        email: values.email,
        group_name: values.group_name,
        password: values.password,
        tier_id: values.tier_id,
        terms_version: terms.version || '1.0',
        consent_timestamp: terms.timestamp || Date.now(),
        browser_user_agent: terms.browserUserAgent || navigator.userAgent,
        browser_language: terms.browserLanguage || navigator.language,
        browser_platform: terms.browserPlatform || navigator.platform,
        consent_data: terms.consentData || {},
      })
      .then((res) => {
        if (res?.data?.payment_required && res?.data?.checkout_url) {
          window.location.href = res.data.checkout_url;
        } else if (res?.data?.email_verification_required) {
          setError('');
          setSuccessMessage(res?.message || 'Check your email for a verification link to activate your account.');
          setEmailSent(true);
          pendingVerifyEmailRef.current = values.email;
        } else if (res?.data?.auth_token) {
          setAuthToken(res.data.auth_token);
          navigate('/', { state: { mfaSetupRequired: true } });
        } else {
          setError(res?.message || 'Sign up failed');
        }
      })
      .catch((err: any) => {
        const resData = err?.data;
        const fieldErrors = resData?.data;
        const firstError =
          fieldErrors &&
          typeof fieldErrors === 'object' &&
          (Object.values(fieldErrors).flat() as string[])[0];
        setError(
          (typeof firstError === 'string' ? firstError : null) ||
            resData?.message ||
            err?.message ||
            'Sign up failed'
        );
      })
      .finally(() => setIsSubmitting(false));
  };

  const submitEnterpriseInquiry = (e?: React.FormEvent | React.MouseEvent) => {
    e?.preventDefault();
    setEnterpriseError('');
    const email = enterpriseEmail.trim();
    if (!email) {
      setEnterpriseError('Contact email is required.');
      return;
    }
    setEnterpriseSubmitting(true);
    api
      .enterpriseInquiry({
        email,
        phone_number: enterprisePhone.trim() || undefined,
      })
      .then((res) => {
        if (res?.success) {
          setEnterpriseSuccess(true);
          setEnterpriseError('');
        } else {
          setEnterpriseError(res?.message || 'Something went wrong.');
        }
      })
      .catch((err: any) => {
        setEnterpriseError(err?.data?.message || err?.message || 'Failed to submit. Please try again.');
      })
      .finally(() => setEnterpriseSubmitting(false));
  };

  const verifyNpi = () => {
    setNpiError('');
    setNpiData(null);
    const num = npiInput.trim();
    if (!/^\d{10}$/.test(num)) {
      setNpiError('NPI must be exactly 10 digits');
      return;
    }
    setNpiVerifying(true);
    api.validateNpi(num)
      .then((res: any) => {
        if (res?.success && res?.data) {
          setNpiData(res.data);
        } else {
          setNpiError(res?.message || 'NPI not found or not eligible');
        }
      })
      .catch((err: any) => {
        setNpiError(err?.data?.message || err?.message || 'NPI verification failed');
      })
      .finally(() => setNpiVerifying(false));
  };

  return (
    <Wrapper>
      <Card>
        <Title>Create Account</Title>
        <Formik
          initialValues={{
            first_name: '',
            last_name: '',
            email: '',
            group_name: '',
            password: '',
            confirm_password: '',
            tier_id: 1,
          }}
          validationSchema={getValidationSchema()}
          onSubmit={(values) => {
            setError('');
            if (!hasUserAgreedToTerms()) {
              setShowTermsModal(true);
              return;
            }
            if (!npiData) {
              setError('Please verify your NPI number before signing up');
              return;
            }
            submitSignup({
              first_name: values.first_name,
              last_name: values.last_name,
              email: values.email,
              group_name: values.group_name,
              password: values.password,
              tier_id: values.tier_id,
            });
          }}
        >
          {({ values, errors, touched, handleChange, handleBlur, setFieldValue }) => (
            <Form>
              <Label>Subscription tier *</Label>
              <Select name="tier_id" value={values.tier_id} onChange={(e) => setFieldValue('tier_id', Number(e.target.value))}>
                {tiers.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
                {tiers.length === 0 && <option value={1}>Free</option>}
              </Select>

              {values.tier_id === 4 ? (
                <>
                  <p style={{ fontSize: 14, color: '#6b7280', marginBottom: 16 }}>
                    Please leave us your contact email and phone number. Our team will reach out to you for setting up your Enterprise account.
                  </p>
                  {enterpriseSuccess ? (
                    <SuccessText>Thank you. Our team will reach out to you shortly.</SuccessText>
                  ) : (
                    <>
                      {enterpriseError && <ErrorText style={{ marginBottom: 12 }}>{enterpriseError}</ErrorText>}
                      <Label>Contact email *</Label>
                      <Input
                        type="email"
                        value={enterpriseEmail}
                        onChange={(e) => setEnterpriseEmail(e.target.value)}
                        placeholder="your@email.com"
                      />
                      <Label>Phone number</Label>
                      <Input
                        type="tel"
                        value={enterprisePhone}
                        onChange={(e) => setEnterprisePhone(e.target.value)}
                        placeholder="(optional)"
                      />
                      <SubmitButton type="button" onClick={submitEnterpriseInquiry} disabled={enterpriseSubmitting}>
                        {enterpriseSubmitting ? 'Sending…' : 'Submit'}
                      </SubmitButton>
                    </>
                  )}
                </>
              ) : (
                <>
                  {successMessage && <SuccessText>{successMessage}</SuccessText>}
                  {error && <ErrorText style={{ marginBottom: 12 }}>{error}</ErrorText>}
                  <Label>First name *</Label>
                  <Input name="first_name" value={values.first_name} onChange={handleChange} onBlur={handleBlur} />
                  {touched.first_name && errors.first_name && <ErrorText>{errors.first_name}</ErrorText>}
                  <Label>Last name *</Label>
                  <Input name="last_name" value={values.last_name} onChange={handleChange} onBlur={handleBlur} />
                  {touched.last_name && errors.last_name && <ErrorText>{errors.last_name}</ErrorText>}
                  <Label>Email *</Label>
                  <Input type="email" name="email" value={values.email} onChange={handleChange} onBlur={handleBlur} />
                  {touched.email && errors.email && <ErrorText>{errors.email}</ErrorText>}
                  <Label>
                    Business or group name {values.tier_id === 3 ? '*' : '(optional)'}
                  </Label>
                  <Input name="group_name" value={values.group_name} onChange={handleChange} onBlur={handleBlur} placeholder="e.g. Acme Clinic" />
                  {touched.group_name && errors.group_name && <ErrorText>{errors.group_name}</ErrorText>}
                  <Label>NPI Number *</Label>
                  <NpiRow>
                    <NpiInput
                      type="text"
                      value={npiInput}
                      maxLength={10}
                      placeholder="10-digit NPI"
                      onChange={(e) => {
                        setNpiInput(e.target.value.replace(/\D/g, ''));
                        setNpiData(null);
                        setNpiError('');
                      }}
                      onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), verifyNpi())}
                    />
                    <NpiVerifyButton type="button" onClick={verifyNpi} disabled={npiVerifying || npiInput.length !== 10}>
                      {npiVerifying ? 'Checking…' : 'Verify'}
                    </NpiVerifyButton>
                  </NpiRow>
                  {npiError && <ErrorText style={{ marginTop: 4, marginBottom: 12 }}>{npiError}</ErrorText>}
                  {npiData && (
                    <NpiInfoBox>
                      <strong>{npiData.npi_display_name}</strong>{npiData.npi_credential ? `, ${npiData.npi_credential}` : ''}<br />
                      {npiData.npi_specialty && <span>{npiData.npi_specialty}</span>}
                      {npiData.npi_state && <span> — {npiData.npi_state}</span>}
                      <br /><span style={{ color: '#059669', fontWeight: 600 }}>NPI verified</span>
                    </NpiInfoBox>
                  )}
                  <Label>Password *</Label>
                  <Input type="password" name="password" value={values.password} onChange={handleChange} onBlur={handleBlur} />
                  {touched.password && errors.password && <ErrorText>{errors.password}</ErrorText>}
                  <Label>Confirm password *</Label>
                  <Input type="password" name="confirm_password" value={values.confirm_password} onChange={handleChange} onBlur={handleBlur} />
                  {touched.confirm_password && errors.confirm_password && <ErrorText>{errors.confirm_password}</ErrorText>}
                  <p style={{ fontSize: 14, color: '#6b7280', marginBottom: 16 }}>
                    You must read and agree to the Terms of Service before signing up. Click the button below to open them.
                  </p>
                  {!hasUserAgreedToTerms() ? (
                    <SubmitButton type="button" onClick={() => setShowTermsModal(true)} disabled={!npiData}>
                      Terms of Service
                    </SubmitButton>
                  ) : (
                    <SubmitButton type="submit" disabled={emailSent || isSubmitting}>
                      {emailSent ? 'Check your email' : isSubmitting ? 'Sending…' : 'Sign Up'}
                    </SubmitButton>
                  )}
                </>
              )}
              <BackLink to="/login">Already have an account? Sign in</BackLink>
              <BackLink to="/">Back to home</BackLink>
            </Form>
          )}
        </Formik>
      </Card>
      <TermsAndConditionsModal show={showTermsModal} onHide={handleHideTermsModal} onAgree={handleAgreeTerms} termsVersion={termsVersion} />
    </Wrapper>
  );
};

export default Signup;
